/**
 * One exclusive filter bar. Map dots follow the playhead; chip counts do not.
 * Suspected ∩ Campaigns is empty by construction — do not AND those.
 */

import { eventClassOf, isFireClass, isPublicClass, matchesShowMode, type MapView } from "./event-state";
import { incidentVisibleAtPlayhead, isImportantEvent } from "./status";
import type { Incident } from "./types";

/** Playhead-visible incidents the public map is allowed to name. */
export function publicIncidents(incidents: Incident[], playheadMs: number): Incident[] {
  return incidents.filter(
    (i) => incidentVisibleAtPlayhead(i.first_seen, playheadMs) && isPublicClass(eventClassOf(i.event_status)),
  );
}

export function matchesAttention(inc: Incident, view: "new" | "rising" | "campaigns", windowUntilMs: number): boolean {
  if (view === "rising") return inc.status === "rapidly_intensifying";
  if (view === "campaigns") return isImportantEvent(inc, windowUntilMs);
  // new <1h against the window clock, not the browser clock.
  return Date.parse(inc.first_seen) >= windowUntilMs - 3600_000;
}

/** Heat follows the same chip as the rings — Major events is not a second Fires map. */
export function heatVisibleIncidents(
  incidents: Incident[],
  playheadMs: number,
  view: MapView,
  windowUntilMs: number,
): Incident[] {
  return visibleIncidents(incidents, playheadMs, view, windowUntilMs);
}

/** Dots for the selected chip. One view, one list. */
export function visibleIncidents(
  incidents: Incident[],
  playheadMs: number,
  view: MapView,
  windowUntilMs: number,
): Incident[] {
  // High Sensitivity: every playhead-visible incident. Class / MW floors stay on the other chips.
  if (view === "fine") {
    return incidents.filter((i) => incidentVisibleAtPlayhead(i.first_seen, playheadMs));
  }
  const pub = publicIncidents(incidents, playheadMs);
  if (view === "new" || view === "rising" || view === "campaigns") {
    return pub.filter((i) => matchesAttention(i, view, windowUntilMs));
  }
  return pub.filter((i) => matchesShowMode(eventClassOf(i.event_status), view));
}

export type ChipCounts = {
  fires: number;
  suspected: number;
  campaigns: number;
  rising: number;
  new: number;
};

/** LIVE totals for the header. Replay / scrub must not change these. */
export function chipCounts(incidents: Incident[], windowUntilMs: number): ChipCounts {
  const pub = publicIncidents(incidents, windowUntilMs);
  return {
    fires: pub.filter((i) => isFireClass(eventClassOf(i.event_status))).length,
    suspected: pub.filter((i) => eventClassOf(i.event_status) === "suspected").length,
    campaigns: pub.filter((i) => matchesAttention(i, "campaigns", windowUntilMs)).length,
    rising: pub.filter((i) => matchesAttention(i, "rising", windowUntilMs)).length,
    new: pub.filter((i) => matchesAttention(i, "new", windowUntilMs)).length,
  };
}
